import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  AcessoLogin,
  AcessoRefreshToken,
  UsuarioAdicionar,
} from "../services/Identity.Service";
import { ILogin } from "../interfaces/ILogin";
import { IToken } from "../interfaces/IToken";
import { SigninDto } from "../pages/Publica/SignIn/Signin.Dto";
import jwtDecode from "jwt-decode";
import { UserRole } from "../enum/UserRole";
import ApiResponse from "src/interfaces/ApiResponse";

interface IAuthContext {
  user: IToken | null;
  isLogged: boolean;
  isLoaded: boolean;
  role: UserRole;
  refreshToken: () => Promise<void | string>;
  logIn: (login: ILogin) => Promise<ApiResponse>;
  signIn: (signin: SigninDto) => Promise<ApiResponse>;
  logOut: () => void;
  userId: number;
  empresaId: number;
}

interface ITokenDecode {
  name: string;
  exp: number;
  role: string;
  nameid: number;
  empresaid: number;
}

export const AuthContext = createContext<IAuthContext>({} as IAuthContext);

export const AuthProvider = ({ children }: any) => {
  const [user, setUser] = useState<IToken | null>(null);
  const [isLoaded, setIsLoaded] = useState<boolean>(false);

  const isAccessTokenValid = useCallback((token: IToken | null): boolean => {
    if (!token?.token_access) return false;
    try {
      const decodeAccessToken = jwtDecode<ITokenDecode>(token.token_access);
      if (!decodeAccessToken?.exp) return false;
      return Date.now() < decodeAccessToken.exp * 1000;
    } catch {
      return false;
    }
  }, []);

  const isRefreshTokenValid = useCallback((token: IToken | null): boolean => {
    if (!token?.token_refresh) return false;
    try {
      const decodeRefreshToken = jwtDecode<ITokenDecode>(token.token_refresh);
      if (!decodeRefreshToken?.exp) return false;
      return Date.now() < decodeRefreshToken.exp * 1000;
    } catch {
      return false;
    }
  }, []);

  const isTokenValid = useCallback(
    (token: IToken | null): boolean => {
      return isAccessTokenValid(token);
    },
    [isAccessTokenValid]
  );

  const redirectToLoginIfNotPublic = useCallback(() => {
    if (
      typeof window !== "undefined" &&
      window.location &&
      window.location.pathname !== "/login" &&
      window.location.pathname !== "/cadastro" &&
      window.location.pathname !== "/confirmar-email"
    ) {
      localStorage.setItem(
        "redirectAfterLogin",
        window.location.pathname + (window.location.search || "")
      );
      window.location.href = "/login";
    }
  }, []);

  useEffect(() => {
    const Load = async () => {
      const userToken = localStorage.getItem("userToken");
      if (userToken) {
        try {
          const parsedToken: IToken = JSON.parse(userToken);
          if (isAccessTokenValid(parsedToken)) {
            setUser(parsedToken);
          } else if (isRefreshTokenValid(parsedToken)) {
            try {
              const response = await AcessoRefreshToken({
                token_refresh: parsedToken.token_refresh,
              });
              if (response.data?.success && response.data?.data) {
                const data = response.data.data;
                localStorage.setItem("userToken", JSON.stringify(data));
                setUser(data);
              } else {
                throw new Error("Falha ao renovar token");
              }
            } catch {
              setUser(null);
              localStorage.removeItem("userToken");
              redirectToLoginIfNotPublic();
            }
          } else {
            setUser(null);
            localStorage.removeItem("userToken");
            redirectToLoginIfNotPublic();
          }
        } catch {
          setUser(null);
          localStorage.removeItem("userToken");
          redirectToLoginIfNotPublic();
        }
      } else {
        redirectToLoginIfNotPublic();
      }
      setIsLoaded(true);
    };
    Load();
  }, [isAccessTokenValid, isRefreshTokenValid, redirectToLoginIfNotPublic]);

  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === "userToken") {
        if (e.newValue) {
          try {
            const parsedToken: IToken = JSON.parse(e.newValue);
            if (isAccessTokenValid(parsedToken)) {
              setUser(parsedToken);
            }
          } catch {
            // Ignora erro de JSON
          }
        } else {
          setUser(null);
        }
      }
    };

    window.addEventListener("storage", handleStorageChange);
    return () => {
      window.removeEventListener("storage", handleStorageChange);
    };
  }, [isAccessTokenValid]);

  const refreshToken = useCallback(async () => {
    try {
      const userTokenStr = localStorage.getItem("userToken");
      const currentToken: IToken | null = userTokenStr
        ? JSON.parse(userTokenStr)
        : user;

      if (!currentToken?.token_refresh) {
        throw new Error("Usuário não autenticado");
      }

      const response = await AcessoRefreshToken({
        token_refresh: currentToken.token_refresh,
      });

      if (response.data.success) {
        const data = response.data.data;
        localStorage.setItem("userToken", JSON.stringify(data));
        setUser(data);
      } else {
        throw new Error("Falha ao renovar o token");
      }
    } catch (error: any) {
      setUser(null);
      const errors = error.response?.data?.errors || [];
      const errorMessage =
        errors.length > 0 ? errors[0].message : "Ocorreu um erro na requisição";
      return errorMessage;
    }
  }, [user]);

  const logIn = useCallback(async (login: ILogin) => {
    const response = await AcessoLogin(login);

    if (response.success) {
      const data = response.data;
      localStorage.setItem("userToken", JSON.stringify(data));
      setUser(data);
    }
    return response;
  }, []);

  const signIn = useCallback(async (signin: SigninDto) => {
    const response = await UsuarioAdicionar(signin);
    return response.data;
  }, []);

  const logOut = useCallback(async () => {
    setUser(null);
    localStorage.removeItem("userToken");
  }, []);

  const getUserRole = useCallback((): UserRole => {
    if (user === null) return UserRole.User;
    if (isTokenValid(user)) {
      const decodeAccessToken = jwtDecode<ITokenDecode>(user.token_access);
      if (decodeAccessToken && decodeAccessToken.role) {
        return decodeAccessToken.role as UserRole;
      }
    }
    return UserRole.User;
  }, [user, isTokenValid]);

  const getEmpresaId = useCallback((): number => {
    const userToken = localStorage.getItem("userToken");
    if (userToken) {
      const parsedToken: IToken = JSON.parse(userToken);
      if (isTokenValid(parsedToken)) {
        const decodeAccessToken = jwtDecode<ITokenDecode>(
          parsedToken.token_access
        );
        if (decodeAccessToken && decodeAccessToken.empresaid) {
          return decodeAccessToken.empresaid;
        }
      }
    }
    return 0;
  }, [user]);

  const getUserId = useCallback((): number => {
    const userToken = localStorage.getItem("userToken");
    if (userToken) {
      const parsedToken: IToken = JSON.parse(userToken);
      if (isTokenValid(parsedToken)) {
        const decodeAccessToken = jwtDecode<ITokenDecode>(
          parsedToken.token_access
        );
        if (decodeAccessToken && decodeAccessToken.nameid) {
          return decodeAccessToken.nameid;
        }
      }
    }
    return 0;
  }, [user]);

  const value = useMemo(
    () => ({
      user: user,
      isLogged: user !== null && isTokenValid(user),
      isLoaded: isLoaded,
      role: getUserRole(),
      refreshToken,
      logIn,
      signIn,
      logOut,
      userId: getUserId(),
      empresaId: getEmpresaId(),
    }),
    [user, logIn, signIn, logOut, getUserRole, isTokenValid, refreshToken]
  );
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const AutenticacaoModule = () => useContext(AuthContext);
